// =============================================
// MI ESTADO DE CUENTA - PROPIETARIO (ACTUALIZADO)
// Muestra TODAS las deudas del año (pagadas y pendientes)
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const OwnerStatement = () => {
  const navigate = useNavigate();
  const [monthlyStatus, setMonthlyStatus] = useState([]);
  const [totals, setTotals] = useState({
    total_quota: 0,
    total_paid: 0,
    total_pending: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [searchYearsBack, setSearchYearsBack] = useState(3);

  useEffect(() => {
    loadSystemParameters();
  }, []);

  useEffect(() => {
    loadDebtsStatus();
  }, [year]);

  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/system-parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');
      
      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  const loadDebtsStatus = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log(`📊 Cargando estado de deudas para año ${year}...`);
      
      const response = await api.get(`/owner-portal/debts-status?year=${year}`);
      
      console.log('✅ Respuesta recibida:', response.data);
      
      if (response.data.success) {
        setMonthlyStatus(response.data.monthly_status || []);
        setTotals(response.data.totals || {
          total_quota: 0,
          total_paid: 0,
          total_pending: 0
        });
      } else {
        setError('No se pudo cargar el estado de cuenta');
      }
    } catch (err) {
      console.error('❌ Error al cargar estado de deudas:', err);
      setError('Error al cargar estado de cuenta: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const getMonthName = (month) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1] || '-';
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: searchYearsBack }, (_, i) => currentYear - i);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/owner/dashboard')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">
            Mi Estado de Cuenta
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PdfIcon />}
          onClick={() => alert('Descarga de PDF en desarrollo')}
        >
          Descargar PDF
        </Button>
      </Box>

      {/* Filtros y Resumen */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'info.light', height: '100%' }}>
              <Typography variant="body2" color="white">Total Cuota</Typography>
              <Typography variant="h6" color="white" fontWeight="bold">
                {formatCurrency(totals.total_quota)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'success.light', height: '100%' }}>
              <Typography variant="body2" color="white">Total Pagado</Typography>
              <Typography variant="h6" color="white" fontWeight="bold">
                {formatCurrency(totals.total_paid)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: totals.total_pending > 0 ? 'error.light' : 'success.light', height: '100%' }}>
              <Typography variant="body2" color="white">Total Pendiente</Typography>
              <Typography variant="h6" color="white" fontWeight="bold">
                {formatCurrency(totals.total_pending)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mes</TableCell>
                <TableCell>Año</TableCell>
                <TableCell align="right">Cuota</TableCell>
                <TableCell align="right">Pagado</TableCell>
                <TableCell align="right">Pendiente</TableCell>
                <TableCell align="center"># Pagos</TableCell>
                <TableCell align="center">Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyStatus.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {getMonthName(item.month)}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.year}</TableCell>
                  <TableCell align="right">{formatCurrency(item.quota)}</TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {formatCurrency(item.paid)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color={item.pending > 0.01 ? 'error.main' : 'success.main'} fontWeight="bold">
                      {formatCurrency(item.pending)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.payment_count || 0} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {item.status === 'pagada' ? (
                      <Chip 
                        icon={<CheckIcon />}
                        label="Pagado" 
                        color="success" 
                        size="small"
                      />
                    ) : (
                      <Chip 
                        icon={<WarningIcon />}
                        label="Pendiente" 
                        color="error" 
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {/* Fila de totales */}
              {monthlyStatus.length > 0 && (
                <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                  <TableCell colSpan={2}>TOTALES</TableCell>
                  <TableCell align="right">{formatCurrency(totals.total_quota)}</TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {formatCurrency(totals.total_paid)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color={totals.total_pending > 0 ? 'error.main' : 'success.main'} fontWeight="bold">
                      {formatCurrency(totals.total_pending)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {monthlyStatus.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No hay deudas registradas para {year}
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </Container>
  );
};

export default OwnerStatement;
